import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BaseDashboard } from '../../models/dashboard/BaseDashboard';
import { FormSearch } from '../../models/dashboard/FormStatus';
import { UtilityService } from '../utility.service';
import { UserWorkflowFormView } from 'app/models/form/UserWorkflowFormView';
import { LoadingService } from '../loading.service';

@Injectable({
	providedIn: 'root',
})
export class UserWorkflowFormService {
	headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0' });

	private readonly urlPath = `${environment.apiUrl}user-management/clients/`;

	constructor(
		private httpClient: HttpClient,
		private utilityService: UtilityService,
		private loadingService: LoadingService
	) {}

	createForm(clientId: string, request: any): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/forms/user-form`, this.setupRequest(request)).pipe();
	}

	private setupRequest(request: any) {
		const formData: FormData = new FormData();

		formData.append('model', JSON.stringify(request));
		return formData;
	}

	updateForm(clientId: string, id: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/forms/user-form/${id}`, this.setupRequest(request))
			.pipe();
	}
	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/forms/user-form/${formId}/docs/${docId}`);
	}
	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/forms/user-form/${id}`).pipe();
	}

	getAllForms(clientId: string, request: any): Observable<any[]> {
		return this.httpClient.post<any[]>(`${this.urlPath}${clientId}/forms/user-form/all`, request).pipe();
	}
	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/user-form/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/user-form/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${this.urlPath}${clientId}/forms/user-form/users/${userId}?${this.utilityService.objectToQueryString(
			search
		)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/forms/user-form/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/forms/user-form/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/forms/user-form/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<UserWorkflowFormView> {
		return this.httpClient
			.post<UserWorkflowFormView>(`${this.urlPath}${clientId}/forms/user-form/${id}/approval`, {
				userId,
			})
			.pipe();
	}
	printPDF(htmlContentElement: HTMLElement): Observable<boolean> {
		return new Observable<boolean>((observer) => {
			this.loadingService.OnLoading.emit(true);

			const iframe = document.createElement('iframe');
			iframe.style.position = 'fixed';
			iframe.style.right = '0';
			iframe.style.bottom = '0';
			iframe.style.width = '0';
			iframe.style.height = '0';
			iframe.style.border = '0';
			document.body.appendChild(iframe);

			const doc = iframe.contentDocument || iframe.contentWindow?.document;
			if (!doc) {
				this.loadingService.OnLoading.emit(false);
				observer.next(false);
				observer.complete();
				return;
			}

			const clone = htmlContentElement.cloneNode(true) as HTMLElement;

			// === 1. Copy input states ===
			const origInputs = htmlContentElement.querySelectorAll('input, textarea, select');
			const cloneInputs = clone.querySelectorAll('input, textarea, select');
			origInputs.forEach((origEl, i) => {
				const cloneEl = cloneInputs[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
				if (!cloneEl) return;

				if (origEl instanceof HTMLInputElement) {
					if (origEl.type === 'checkbox' || origEl.type === 'radio') {
						(cloneEl as HTMLInputElement).checked = origEl.checked;
					} else {
						(cloneEl as HTMLInputElement).value = origEl.value;
					}
				} else if (origEl instanceof HTMLTextAreaElement) {
					(cloneEl as HTMLTextAreaElement).value = origEl.value;
				} else if (origEl instanceof HTMLSelectElement) {
					Array.from(origEl.options).forEach((option, index) => {
						(cloneEl as HTMLSelectElement).options[index].selected = option.selected;
					});
				}
			});

			// === 2. Copy <style> and <link rel="stylesheet"> ===
			const headHTML = Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]'))
				.map((node) => node.outerHTML)
				.join('\n');

			doc.open();
			doc.write(`
			<!DOCTYPE html>
			<html>
			<head>
				<title>Print</title>
				${headHTML}
			</head>
			<body>
				<div id="print-root"></div>
			</body>
			</html>
		`);
			doc.close();

			const onLoad = () => {
				const printRoot = doc.getElementById('print-root');
				if (printRoot) {
					printRoot.appendChild(clone);
				}

				const cleanup = () => {
					document.body.removeChild(iframe);
					this.loadingService.OnLoading.emit(false);
					observer.next(true);
					observer.complete();
				};

				if (iframe.contentWindow) {
					iframe.contentWindow.onafterprint = cleanup;
				}
				setTimeout(cleanup, 10000); // fallback
				iframe.contentWindow?.focus();
				iframe.contentWindow?.print();
			};

			iframe.onload = onLoad;
		});
	}
}
